import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, HStack, Skeleton, Text, VStack } from '@bonusly/design-system';

import type { BizyUserConversation } from '../../../schemas/bizy-conversations';
import { ConversationCard } from './conversation-card';

interface ConversationListProps {
	conversations: BizyUserConversation[] | undefined;
	isLoading: boolean;
	page: number;
	totalPages: number;
	totalCount: number;
	onSelectConversation: (conversationId: string) => void;
	onPageChange: (page: number) => void;
}

export const ConversationList: FC<ConversationListProps> = ({ conversations, isLoading, page, totalPages, totalCount, onSelectConversation, onPageChange }) => {
	const { t } = useTranslation('bizy_history');

	if (isLoading && !conversations) {
		return (
			<VStack spacing={3} align="stretch">
				{Array.from({ length: 5 }).map((_, i) => (
					<Skeleton key={i} height="80px" borderRadius="md" />
				))}
			</VStack>
		);
	}

	return (
		<VStack spacing={4} align="stretch">
			{/* Conversation cards */}
			<VStack spacing={3} align="stretch">
				{conversations?.map((conversation) => (
					<ConversationCard key={conversation.conversationId} conversation={conversation} onClick={onSelectConversation} />
				))}
			</VStack>

			{/* Pagination */}
			{totalPages > 1 && (
				<Flex justify="space-between" align="center" pt={2}>
					<Text fontSize="sm" color="text.soft">
						{t('pagination.showing', { page, totalPages, totalCount })}
					</Text>
					<HStack spacing={2}>
						<Button size="sm" variant="outline" isDisabled={page === 1} onClick={() => onPageChange(page - 1)}>
							{t('pagination.previous')}
						</Button>
						<Button size="sm" variant="outline" isDisabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
							{t('pagination.next')}
						</Button>
					</HStack>
				</Flex>
			)}
		</VStack>
	);
};
